import { useState } from "react";
import {
  TriangleAlert,
  CircleAlert,
  ClipboardCheckIcon,
  Code2,
  ArrowUpRight,
  X,
} from "lucide-react";
import { useAuth } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ConfirmPopover } from "@/components/ui/confirm-popover";
import { Badge } from "@/components/ui/badge";
import { useCampaignStore, useCreatorsStore } from "@/stores";
import { phaseDisplayLabel } from "@/components/creators-table/phase/blocks";
import { useCreatorsTableContextOptional } from "@/components/creators-table/CreatorsTableContext";
import { pipelineForCreator } from "@/lib/eve";

const VITE_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;

interface InboxAlertsProps {
  creator_id: string;
}

export default function InboxAlerts({ creator_id }: InboxAlertsProps) {
  const { creators, dismissHandoff } = useCreatorsStore();
  const storeCampaign = useCampaignStore((s) => s.campaign);
  // Inside a creators table the row's own campaign labels the phases (the
  // org-wide Global CRM mixes campaigns); the campaign Inbox tab has none.
  const tableContext = useCreatorsTableContextOptional();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [isDismissing, setIsDismissing] = useState(false);
  const creator = creators.find((c) => c._id === creator_id);
  const campaign = tableContext ? tableContext.campaignFor(creator) : storeCampaign;

  // Amy-v2 review states: a submission is parked for the team's verdict.
  // If she couldn't watch the file, escalate the banner to a warning with
  // the failure marker — same idiom as the handoff banner above it.
  const status = creator?.status ?? "";
  const isReview = status.endsWith("-review");
  // Labels come from the creator's own tier pipeline.
  const reviewLabel = isReview
    ? phaseDisplayLabel(pipelineForCreator(campaign, creator), status.slice(0, -"-review".length))
    : "";
  const latestArtifact = creator?.eve_phase?.artifacts?.length
    ? creator.eve_phase.artifacts[creator.eve_phase.artifacts.length - 1]
    : undefined;
  const unwatchedMedia = isReview
    ? (latestArtifact?.media ?? []).filter((m) => !m.watched)
    : [];

  const handleDismissHandoff = async () => {
    if (isDismissing) return;
    setIsDismissing(true);
    const token = await getToken();
    if (token) {
      await dismissHandoff(creator_id, token);
    }
    setIsDismissing(false);
  };

  const isHandOff = creator?.hand_off_to_user || false;
  const error = creator?.error || "";
  const unansweredQuestion = creator?.unanswered_question || "";
  const debugInfo = creator?.debug_info || "";
  const resourcesDebugInfo = creator?.resources_debug_info || "";
  const resources = creator?.resources || "";

  return (
    <div className="flex xl:flex-row flex-col w-full bg-background">
      {isHandOff && (
        <>
          <Alert
            variant="warning"
            className="border-t-0 border-l-0 border-r-0 rounded-none"
          >
            <AlertTitle>
              <TriangleAlert aria-hidden="true" />
              Turned over to human:
            </AlertTitle>
            {unansweredQuestion && (
              <AlertDescription>
                • Unanswered Question: {unansweredQuestion}
              </AlertDescription>
            )}
            {debugInfo && (
              <AlertDescription>• Debug Info: {debugInfo}</AlertDescription>
            )}
            <ConfirmPopover
              title="Dismiss handoff?"
              description={
                <>
                  Clear the handoff without recording any new knowledge for
                  Amy. The unanswered question and debug info will be lost.
                  <Badge variant="warning">Autopilot will turn ON</Badge>
                </>
              }
              confirmLabel={<><X /> Dismiss Handoff</>}
              confirmVariant="warning"
              onConfirm={handleDismissHandoff}
              disabled={isDismissing}
            >
              <Button
                variant="warning-ghost"
                size="icon"
                className="absolute top-1.5 right-1.5 size-6.5"
                disabled={isDismissing}
                aria-label="Dismiss handoff"
              >
                <X className="size-4" />
              </Button>
            </ConfirmPopover>
          </Alert>
          {VITE_ENVIRONMENT === "development" && resourcesDebugInfo && (
            <Alert
              variant="info"
              className="border-t-0 border-l-0 border-r-0 rounded-none"
            >
              <AlertTitle>
                <Code2 aria-hidden="true" />
                Resources Debug info:
              </AlertTitle>
              <AlertDescription>
                • Debug info:{" "}
                {resourcesDebugInfo.length > 100
                  ? resourcesDebugInfo.substring(0, 100) + "..."
                  : resourcesDebugInfo}
              </AlertDescription>
              <AlertDescription>
                • Resources:{" "}
                {resources.length > 100
                  ? resources.substring(0, 100) + "..."
                  : resources}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {isReview && (
        <Alert
          variant={unwatchedMedia.length > 0 ? "warning" : "indigo"}
          className="border-t-0 border-l-0 border-r-0 rounded-none"
        >
          <AlertTitle>
            {unwatchedMedia.length > 0 ? (
              <TriangleAlert aria-hidden="true" />
            ) : (
              <ClipboardCheckIcon aria-hidden="true" />
            )}
            {unwatchedMedia.length > 0
              ? "Amy could not watch the submitted file:"
              : `${reviewLabel} submission awaiting your review`}
          </AlertTitle>
          {unwatchedMedia.map((m) => (
            <AlertDescription key={m.url}>• {m.notes}</AlertDescription>
          ))}
          {unwatchedMedia.length > 0 && (
            <AlertDescription>
              • A review task was created — Amy never judges files she
              couldn't watch, so the verdict is yours.
            </AlertDescription>
          )}
          <Button
            variant="warning"
            size="sm"
            className="absolute top-0.5 right-0.5 h-6.5"
            scope={false}
            onClick={() => navigate(`/dashboard/tasks?action=${creator_id}`)}
          >
            Review <ArrowUpRight className="size-3.5" />
          </Button>
        </Alert>
      )}

      {error.length > 0 && (
        <Alert
          variant="destructive"
          className="border-t-0 border-l-0 border-r-0 rounded-none"
        >
          <AlertTitle>
            <CircleAlert aria-hidden="true" />
            Error in creator's inbox:
          </AlertTitle>
          <AlertDescription>• {error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
